import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

const SLUG = "pikler-kolmio";
const H1 = "Pikler-kolmio — kiipeilyteline lapselle";
const TITLE = "Pikler-kolmiot ja kiipeilytelineet lapsille | Montessori24";
const DESC =
  "Tutustu Pikler-kolmioihin, ramppeihin ja lasten sisäkiipeilytelineisiin. Vertaa mittoja, ikäsuosituksia ja ominaisuuksia helposti.";

const FAQS = [
  {
    q: "Mikä on Pikler-kolmio?",
    a: "Pikler-kolmio on unkarilaisen lastenlääkäri Emmi Piklerin kehittämä kolmion muotoinen kiipeilyteline, jonka avulla lapsi harjoittelee liikkumista omaan tahtiinsa. Se tukee tasapainoa, koordinaatiota ja itsenäistä liikkumista turvallisesti sisätiloissa.",
  },
  {
    q: "Minkä ikäiselle Pikler-kolmio sopii?",
    a: "Pikler-kiipeilytelineiden ikäsuositukset vaihtelevat tuotteen ja valmistajan mukaan. Tarkista aina tuotekohtainen ikäsuositus tuotesivulta. Yleisesti pienin lapsi käyttää alapuita pystyyn nousuun, taapero kiipeää ylös ja isompi lapsi rakentaa kolmiosta majan tai temppuradan.",
  },
  {
    q: "Kuinka paljon Pikler-kolmio painaa ja tarvitseeko se paljon tilaa?",
    a: "Mallista riippuen kolmio vie käytössä tyypillisesti noin 80×80 cm lattiatilaa ja monet mallit taittuvat kasaan säilytystä varten. Tuotekohtaiset mitat ja paino löydät tuotesivun tuotetiedoista.",
  },
  {
    q: "Pikler-kolmio vai kiipeilykaari — kumpi ensin?",
    a: "Pieni vauva (6–12 kk) hyötyy eniten matalasta kaaresta, joka toimii kelluvana leikkielementtinä. 1-vuotiaasta alkaen kolmio + ramppi on monikäyttöisempi. Setti (kolmio + kaari + ramppi) kestää pisimpään, sillä osat yhdistyvät radoiksi.",
  },
  {
    q: "Onko Pikler-kolmio turvallinen?",
    a: "Kyllä, kun se on aikuisen kokoama oikein ja lapsi kiipeilee valvotusti pehmeällä alustalla. Ikäsuositus, enimmäiskuormitus ja turvallisuustiedot vaihtelevat valmistajan mukaan — tarkista ne aina tuotteen omalta sivulta.",
  },
];

export const Route = createFileRoute("/pikler-kolmio")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: PiklerPage,
});

function PiklerPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Pikler-kolmiot"
      title={H1}
      intro="Pikler-kolmio on turvallinen sisäkiipeilyteline, joka kasvaa lapsen mukana vauvasta koululaiseen. Valmistus Euroopassa. Tarkista tuotekohtainen ikäsuositus ja turvallisuustiedot tuotesivulta. Kolmio, kaari ja ramppi yhdistyvät loputtomiksi liikuntaradoiksi."
      kauppaCategory="climbing"
      match={(t) => /kiipeil|kiipeily|kolmio|kaari|kolmikulm|3-?kulm|climbing|climber|triangle|arch|3-?angle|pikler/i.test(t)}
      relatedChips={[
        { label: "Kaikki kiipeilytelineet", to: "/kiipeilyteline" },
        { label: "Oppimistornit", to: "/oppimistorni" },
        { label: "Oppimistorni-opas", to: "/oppimistorni-opas" },
        { label: "Pikler-opas", to: "/montessori-koulu/pikler-kiipeilyteline-opas" },
      ]}
      body={[
        {
          heading: "Miksi Pikler-menetelmä toimii",
          paragraphs: [
            "Emmi Pikler tutki 1900-luvun puolivälissä, miten lapsi oppii liikkumaan kun aikuinen ei sekaannu. Tulos oli selvä: kun lapsi saa itse valita mihin kiivetä ja milloin, hän ei ota liian isoja riskejä eikä satuta itseään yhtä helposti kuin aikuisen nostamana. Kolmio on tämän ajatuksen konkretisointi — laite, jossa alin puola on pienen ulottuvilla ja jokainen seuraava askel on lapsen oman kehityksen päätös.",
            "Käytännössä sama teline palvelee montaa vuotta: pienin lapsi vetää itsensä pystyyn alimmasta puolasta, taapero kiipeää ylös ja liukuu rampilla alas, isompi rakentaa kolmiosta majan peiton avulla. Ikäsuositus vaihtelee Pikler-mallin ja valmistajan mukaan — tarkista aina tuotekohtainen ikäsuositus ennen käyttöä.",
          ],
        },
        {
          heading: "Kolmio, kaari ja ramppi — mikä sopii kenellekin",
          paragraphs: [
            "Kolmio on perusrunko. Kaksi säätöasentoa (matala + korkea) tekevät siitä käyttökelpoisen taaperoiästä esikouluun. Kolmion voi taittaa kasaan säilytystä varten.",
            "Kaari on paras aivan pienelle. Se toimii keinualustana, majana ja kiikkuvana ryömimiskaarena. Tarkista tuotekohtainen ikäsuositus tuotesivulta.",
            "Ramppi liitetään kolmion tai kaaren päälle. Toinen puoli on liukumäki, toinen kiipeilypuola. Ramppi tekee kokonaisuudesta liikuntaradan.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}